import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import ReactSelect from 'react-select'

import Toast from 'components/atoms/Toast'
import DashboardContent from 'components/lab/atoms/DashboardContent'
import routes from 'components/lab/constants/routes'
import { UserPermission } from 'components/lab/user/permissions'
import { useLabPermission } from 'hooks/useLabPermission'
import {
  RetellOutboundSettings as RetellOutboundSettingsDto,
  TransferLimitRule,
  useGetRetellOutboundSettingsQuery,
  usePlaceRetellOutboundTestCallMutation,
  useUpdateRetellOutboundSettingsMutation,
} from 'store/services/retellOutboundSettings'

const TEST_CALL_BUCKETS = [
  { value: 'HST_PATIENT_SCHEDULING', label: 'HST patient scheduling' },
  { value: 'HST_PATIENT_TESTING', label: 'HST patient testing' },
  { value: 'HST_PENDING_RETURN', label: 'HST pending return' },
  { value: 'POX_DELIVERED', label: 'POX delivered (testing reminder)' },
  { value: 'POX_PENDING_RETURN', label: 'POX pending return' },
]

const RULE_BUCKET_OPTIONS = [
  { value: 'HST_PATIENT_SCHEDULING', label: 'HST patient scheduling' },
  { value: 'HST_PATIENT_TESTING', label: 'HST patient testing' },
  { value: 'HST_PENDING_RETURN', label: 'HST pending return' },
  { value: 'POX_DELIVERED', label: 'POX delivered' },
  { value: 'POX_PENDING_RETURN', label: 'POX pending return' },
]

const emptySettings: RetellOutboundSettingsDto = {
  id: 'patient_reminders:GLOBAL:global',
  useCase: 'patient_reminders',
  displayName: 'Patient Reminders',
  scopeType: 'GLOBAL',
  scopeId: 'global',
  enabled: false,
  dryRun: true,
  paused: false,
  outboundAgentId: '',
  fromNumber: '',
  defaultTimezone: 'America/New_York',
  weekendsEnabled: false,
  officeHours: {
    start: '09:00',
    end: '17:00',
  },
  patientContactHours: {
    start: '09:00',
    end: '20:00',
  },
  maxCallsPerDay: null,
  maxCallsPerOfficePerHour: null,
  transferLimitRules: [],
  concurrencyRetrySeconds: 60,
  testCallEnabled: false,
  featureEnabled: false,
  resourceMappings: [],
}

type MutationError = {
  data?: {
    message?: string
  }
}

const numberOrNull = (value: number | null) => (value ? Number(value) : null)

const RetellOutboundSettings = () => {
  const canManage = useLabPermission(UserPermission.MANAGE_RETELL_OUTBOUND_SETTINGS)
  const { data, isLoading } = useGetRetellOutboundSettingsQuery()
  const [updateSettings, { isLoading: saving }] = useUpdateRetellOutboundSettingsMutation()
  const [placeTestCall, { isLoading: placingTestCall }] = usePlaceRetellOutboundTestCallMutation()
  const [form, setForm] = useState<RetellOutboundSettingsDto>(emptySettings)
  const [testOrderId, setTestOrderId] = useState('')
  const [testBucket, setTestBucket] = useState('')
  const [testToNumber, setTestToNumber] = useState('')

  useEffect(() => {
    if (data) {
      setForm(data)
    }
  }, [data])

  const setValue = <T extends keyof RetellOutboundSettingsDto>(
    key: T,
    value: RetellOutboundSettingsDto[T],
  ) => {
    setForm((current) => ({ ...current, [key]: value }))
  }

  const setOfficeHours = (key: 'start' | 'end', value: string) => {
    setForm((current) => ({
      ...current,
      officeHours: { ...current.officeHours, [key]: value },
    }))
  }

  const setPatientContactHours = (key: 'start' | 'end', value: string) => {
    setForm((current) => ({
      ...current,
      patientContactHours: { ...current.patientContactHours, [key]: value },
    }))
  }

  const addRule = () => {
    setForm((current) => ({
      ...current,
      transferLimitRules: [
        ...current.transferLimitRules,
        { id: String(Date.now()) + Math.random().toString(36).slice(2), label: '', buckets: [], maxTransfers: 5, windowMinutes: 10 },
      ],
    }))
  }

  const updateRule = (id: string, patch: Partial<TransferLimitRule>) => {
    setForm((current) => ({
      ...current,
      transferLimitRules: current.transferLimitRules.map((rule) =>
        rule.id === id ? { ...rule, ...patch } : rule,
      ),
    }))
  }

  const removeRule = (id: string) => {
    setForm((current) => ({
      ...current,
      transferLimitRules: current.transferLimitRules.filter((rule) => rule.id !== id),
    }))
  }

  const getErrorMessage = (error: unknown) =>
    (error as MutationError)?.data?.message || 'Failed to save Retell outbound settings.'

  const save = async () => {
    try {
      await updateSettings({
        ...form,
        outboundAgentId: form.outboundAgentId?.trim() || null,
        fromNumber: form.fromNumber?.trim() || null,
        defaultTimezone: form.defaultTimezone.trim() || 'America/New_York',
        maxCallsPerDay: numberOrNull(form.maxCallsPerDay),
        maxCallsPerOfficePerHour: numberOrNull(form.maxCallsPerOfficePerHour),
        transferLimitRules: form.transferLimitRules,
        concurrencyRetrySeconds: Number(form.concurrencyRetrySeconds) || 60,
      }).unwrap()
      Toast({ type: 'success', label: 'Retell outbound settings saved.' })
    } catch (error: unknown) {
      Toast({ type: 'error', label: getErrorMessage(error) })
    }
  }

  const sendTestCall = async () => {
    const orderId = Number(testOrderId)
    if (!orderId) {
      Toast({ type: 'error', label: 'Enter a valid order ID for the test call.' })
      return
    }
    try {
      const result = await placeTestCall({
        orderId,
        bucket: testBucket || null,
        toNumberOverride: testToNumber.trim() || null,
      }).unwrap()
      Toast({
        type: 'success',
        label: `Test call placed to ${result.toNumber}${result.retellCallId ? ` (call ${result.retellCallId})` : ''}.`,
      })
    } catch (error: unknown) {
      Toast({ type: 'error', label: (error as MutationError)?.data?.message || 'Failed to place test call.' })
    }
  }

  return (
    <DashboardContent
      title="RETELL OUTBOUND SETTINGS"
      icon="fas fa-phone"
      content={
        <div className="card card-primary">
          <div className="card-body">
            <div className="alert alert-info d-flex justify-content-between align-items-center">
              <span>
                Live calling is{' '}
                <strong>{data?.featureEnabled ? 'ACTIVE in this environment' : 'INACTIVE in this environment'}</strong>
                {' '}(controlled by the RETELL_OUTBOUND_FEATURE_ENABLED deploy flag).
              </span>
              <Link to={`${routes.index}${routes.retellOutboundActivity.root}`}>
                View AI Outbound Calls log →
              </Link>
            </div>
            <div className={`alert d-flex justify-content-between align-items-center ${form.paused ? 'alert-danger' : 'alert-secondary'}`}>
              <span>
                Outbound calling is currently <strong>{form.paused ? 'PAUSED' : 'not paused'}</strong>.
                {form.paused && ' No new calls will be placed; already-scheduled attempts are held and will resume automatically once unpaused.'}
              </span>
              <div className="form-check form-switch m-0">
                <input
                  className="form-check-input"
                  disabled={!canManage}
                  type="checkbox"
                  id="pausedToggle"
                  checked={form.paused}
                  onChange={(event) => setValue('paused', event.target.checked)}
                />
                <label className="form-check-label ml-2" htmlFor="pausedToggle">
                  {form.paused ? 'Paused' : 'Pause outbound calling'}
                </label>
              </div>
            </div>
            {isLoading ? (
              <div>Loading...</div>
            ) : (
              <>
                <div className="row">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Use case</label>
                      <input
                        className="form-control"
                        disabled
                        value={form.displayName}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Outbound agent ID</label>
                      <input
                        className="form-control"
                        disabled={!canManage}
                        value={form.outboundAgentId ?? ''}
                        onChange={(event) => setValue('outboundAgentId', event.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>From number</label>
                      <input
                        className="form-control"
                        disabled={!canManage}
                        placeholder="+14157774444"
                        value={form.fromNumber ?? ''}
                        onChange={(event) => setValue('fromNumber', event.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Default timezone</label>
                      <input
                        className="form-control"
                        disabled={!canManage}
                        value={form.defaultTimezone}
                        onChange={(event) => setValue('defaultTimezone', event.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Office hours start</label>
                      <input
                        className="form-control"
                        disabled={!canManage}
                        type="time"
                        value={form.officeHours.start}
                        onChange={(event) => setOfficeHours('start', event.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Office hours end</label>
                      <input
                        className="form-control"
                        disabled={!canManage}
                        type="time"
                        value={form.officeHours.end}
                        onChange={(event) => setOfficeHours('end', event.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Patient contact start</label>
                      <input
                        className="form-control"
                        disabled={!canManage}
                        type="time"
                        value={form.patientContactHours.start}
                        onChange={(event) => setPatientContactHours('start', event.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Patient contact end</label>
                      <input
                        className="form-control"
                        disabled={!canManage}
                        type="time"
                        value={form.patientContactHours.end}
                        onChange={(event) => setPatientContactHours('end', event.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        disabled={!canManage}
                        type="checkbox"
                        checked={form.enabled}
                        onChange={(event) => setValue('enabled', event.target.checked)}
                      />
                      <label className="form-check-label">Enable outbound calls</label>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        disabled={!canManage}
                        type="checkbox"
                        checked={form.dryRun}
                        onChange={(event) => setValue('dryRun', event.target.checked)}
                      />
                      <label className="form-check-label">Dry run mode</label>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        disabled={!canManage}
                        type="checkbox"
                        checked={form.weekendsEnabled}
                        onChange={(event) => setValue('weekendsEnabled', event.target.checked)}
                      />
                      <label className="form-check-label">Allow weekend calls</label>
                    </div>
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Max calls per day</label>
                      <input
                        className="form-control"
                        disabled={!canManage}
                        min={1}
                        type="number"
                        value={form.maxCallsPerDay ?? ''}
                        onChange={(event) => (
                          setValue('maxCallsPerDay', event.target.value ? Number(event.target.value) : null)
                        )}
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Max calls per office/hour</label>
                      <input
                        className="form-control"
                        disabled={!canManage}
                        min={1}
                        type="number"
                        value={form.maxCallsPerOfficePerHour ?? ''}
                        onChange={(event) => (
                          setValue(
                            'maxCallsPerOfficePerHour',
                            event.target.value ? Number(event.target.value) : null,
                          )
                        )}
                      />
                    </div>
                  </div>
                </div>

                {!canManage && (
                  <div className="alert alert-warning mt-3">
                    You do not have permission to update these settings.
                  </div>
                )}

                <div className="card card-outline card-info mt-4">
                  <div className="card-header">
                    <h3 className="card-title">Transfer Capacity</h3>
                  </div>
                  <div className="card-body">
                    <p className="text-muted">
                      Cap how many calls may be transferred to staff within a rolling window, per group of
                      buckets. When a group is over its limit, its calls are rescheduled later the same day
                      until the load subsides. A bucket may belong to only one rule.
                    </p>

                    <div className="form-group" style={{ maxWidth: 260 }}>
                      <label>Concurrency retry (seconds)</label>
                      <input
                        className="form-control"
                        disabled={!canManage}
                        min={1}
                        type="number"
                        value={form.concurrencyRetrySeconds}
                        onChange={(event) =>
                          setValue('concurrencyRetrySeconds', Number(event.target.value) || 60)
                        }
                      />
                      <small className="text-muted">
                        How long to wait before retrying a call that Retell deferred because the
                        parallel-line cap was reached.
                      </small>
                    </div>

                    {form.transferLimitRules.map((rule) => (
                      <div key={rule.id} className="border rounded p-3 mb-3">
                        <div className="row">
                          <div className="col-md-3">
                            <div className="form-group">
                              <label>Label (optional)</label>
                              <input
                                className="form-control"
                                disabled={!canManage}
                                value={rule.label ?? ''}
                                onChange={(event) => updateRule(rule.id, { label: event.target.value })}
                              />
                            </div>
                          </div>
                          <div className="col-md-5">
                            <div className="form-group">
                              <label>Buckets</label>
                              <ReactSelect
                                isMulti
                                isDisabled={!canManage}
                                options={RULE_BUCKET_OPTIONS}
                                value={RULE_BUCKET_OPTIONS.filter((option) =>
                                  rule.buckets.includes(option.value),
                                )}
                                onChange={(selected) =>
                                  updateRule(rule.id, {
                                    buckets: (selected as { value: string }[] | null ?? []).map((option) => option.value),
                                  })
                                }
                              />
                            </div>
                          </div>
                          <div className="col-md-2">
                            <div className="form-group">
                              <label>Max transfers</label>
                              <input
                                className="form-control"
                                disabled={!canManage}
                                min={1}
                                type="number"
                                value={rule.maxTransfers}
                                onChange={(event) =>
                                  updateRule(rule.id, { maxTransfers: Number(event.target.value) || 1 })
                                }
                              />
                            </div>
                          </div>
                          <div className="col-md-2">
                            <div className="form-group">
                              <label>Window (min)</label>
                              <input
                                className="form-control"
                                disabled={!canManage}
                                min={1}
                                type="number"
                                value={rule.windowMinutes}
                                onChange={(event) =>
                                  updateRule(rule.id, { windowMinutes: Number(event.target.value) || 1 })
                                }
                              />
                            </div>
                          </div>
                        </div>
                        {canManage && (
                          <button
                            className="btn btn-sm btn-outline-danger"
                            type="button"
                            onClick={() => removeRule(rule.id)}>
                            Remove rule
                          </button>
                        )}
                      </div>
                    ))}

                    {canManage && (
                      <button className="btn btn-outline-primary" type="button" onClick={addRule}>
                        Add rule
                      </button>
                    )}
                  </div>
                </div>

                <div className="card card-outline card-secondary mt-4">
                  <div className="card-header">
                    <h3 className="card-title">Call resource requirements</h3>
                  </div>
                  <div className="card-body">
                    <p className="text-muted">
                      Each bucket&apos;s calls are scheduled only when every required resource is available.
                      These mappings are defined in code (read-only here); the hours reflect the settings above.
                    </p>
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Bucket</th>
                          <th>Required resources</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data?.resourceMappings ?? []).map((mapping) => (
                          <tr key={mapping.bucket}>
                            <td>
                              <strong>{mapping.bucket}</strong>
                              <div className="text-muted small">{mapping.callReason}</div>
                            </td>
                            <td>
                              {mapping.resources.map((resource) => (
                                <div key={resource.id}>
                                  {resource.label}: {resource.days} {resource.hours} ({resource.timezone})
                                </div>
                              ))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <button
                  className="btn btn-primary mt-3"
                  disabled={!canManage || saving}
                  type="button"
                  onClick={save}>
                  {saving ? 'Saving...' : 'Save Settings'}
                </button>

                {canManage && form.testCallEnabled && (
                  <div className="card card-outline card-warning mt-4">
                    <div className="card-header">
                      <h3 className="card-title">Place test call (dev only)</h3>
                    </div>
                    <div className="card-body">
                      <p className="text-muted">
                        Triggers an on-demand outbound call for this use case against a specific order,
                        bypassing the enable/dry-run/call-window checks. By default the bucket is
                        auto-detected from the order&apos;s current status (matching the scheduled flow);
                        choose a bucket to force a specific scenario. The dialed number must still be on the
                        dev whitelist; use an override number to receive the call yourself.
                      </p>
                      <div className="row">
                        <div className="col-md-3">
                          <div className="form-group">
                            <label>Order ID</label>
                            <input
                              className="form-control"
                              type="number"
                              min={1}
                              value={testOrderId}
                              onChange={(event) => setTestOrderId(event.target.value)}
                            />
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="form-group">
                            <label>Bucket</label>
                            <select
                              className="form-control"
                              value={testBucket}
                              onChange={(event) => setTestBucket(event.target.value)}>
                              <option value="">Auto-detect from order status</option>
                              {TEST_CALL_BUCKETS.map((bucket) => (
                                <option key={bucket.value} value={bucket.value}>
                                  {bucket.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="col-md-5">
                          <div className="form-group">
                            <label>Override to number (optional)</label>
                            <input
                              className="form-control"
                              placeholder="Defaults to patient's number"
                              value={testToNumber}
                              onChange={(event) => setTestToNumber(event.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                      <button
                        className="btn btn-warning"
                        disabled={placingTestCall}
                        type="button"
                        onClick={sendTestCall}>
                        {placingTestCall ? 'Placing call...' : 'Place test call'}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      }
      breadcrumb={[
        {
          label: 'Retell Outbound Settings',
          to: `${routes.index}${routes.retellOutboundSettings.root}`,
        },
      ]}
    />
  )
}

export default RetellOutboundSettings
